import React, { useEffect, useMemo, useState } from 'react';
import { Grid, TextField, Button, Checkbox, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dialogStyles from '../../../../components/dialog/GenericDialog.styles';
import ValidatedField from '../../../../components/inputs/ValidatedField';
import useAuthUser from '../../../auth/hooks/useAuthUser';
import { coerceFaulty, formatName, generateFileName } from '../../utils/meter.helpers';
import { useUpdateMeterFaulty } from '../../hooks/useMeterActions';
import { METER_DATE_RULES, METER_COMMENTS_RULES } from '../../utils/meterValidation';

export default function MeterFaultyTab({ initialData, onSubmit, helpers }) {
  const { close, setLoading } = helpers;
  const [faulty, setFaulty] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [comments, setComments] = useState('');
  const navigate = useNavigate();

  const { user } = useAuthUser();
  const displayUser = useMemo(
    () => formatName(user?.name) || user?.email || 'Unknown User',
    [user]
  );

  const oldFromDate = initialData?.fromDate || '';
  const initialFaulty = useMemo(
    () => coerceFaulty(initialData?.faulty ?? initialData?.Faulty ?? initialData?.IsFaulty ?? false),
    [initialData]
  );

  const { mutate, loading } = useUpdateMeterFaulty();

  useEffect(() => {
    setFaulty(initialFaulty);
    setSelectedDate('');
    setComments('');
    setLoading(false);
  }, [setLoading, initialFaulty]);

  const changeMade = faulty !== initialFaulty;
  const validDate = METER_DATE_RULES.every((r) => r.test(selectedDate));
  const validComments = METER_COMMENTS_RULES.every((r) => r.test(comments));
  const canSubmit = changeMade && validDate && validComments;

  const handleUpdate = async () => {
    if (!canSubmit) return;
    setLoading(true);
    const filename = generateFileName();

    try {
      await mutate({
        reqType: 'MC',
        meterNumber: initialData?.meterNumber,
        oldFromDate,
        selectedDate,
        comments: comments.trim() || null,
        faulty,
        user: displayUser,
        filename,
      });
      onSubmit?.();
      close();
      navigate('/home/structure-logs', { state: { fileName: filename, status: null } });
    } catch (err) {
      alert(`Failed to update meter:\n${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Meter Number"
            fullWidth
            size="small"
            variant="standard"
            value={initialData?.meterNumber || ''}
            InputProps={{ readOnly: true, disableUnderline: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>

        <Grid item xs={12}>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#555', mb: 0.5 }}>Faulty?</Typography>
            <Checkbox
              checked={faulty}
              onChange={(e) => setFaulty(e.target.checked)}
              size="small"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <ValidatedField
            label="Effective Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            rules={METER_DATE_RULES}
          />
        </Grid>

        <Grid item xs={12}>
          <ValidatedField
            label="Comments"
            multiline
            placeholder="Enter comments..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rules={METER_COMMENTS_RULES}
          />
        </Grid>
      </Grid>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
        <Button onClick={close} variant="contained" sx={dialogStyles.btnSecondary} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          disabled={!canSubmit || loading}
          variant="contained"
          sx={dialogStyles.btnPrimary}
        >
          {loading ? 'Updating…' : 'Update'}
        </Button>
      </div>
    </>
  );
}
